import {
  ApiChartAttribute,
  ApiChartLayout,
  ApiChartTemplate,
  ApiChartType,
  ApiDimension,
  ApiMetric,
  ApiOperator,
  ApiYAxisChartType,
} from "@incendium/api";
import { Box, Stack } from "@mui/material";
import CarouselCard from "Components/UI/CarouselCard";
import { StandardAnalyticsChartStatCard } from "features/analytics";
import StandardAnalyticsCard from "features/analytics/components/StandardAnalyticsCard";
import {
  CHART_KEY_DASHBOARD_SPEED_LANDING_BOUNCE,
  CHART_KEY_DASHBOARD_SPEED_LANDING_CWV,
  CHART_KEY_DASHBOARD_SPEED_LANDING_CWV_BAR,
  CHART_KEY_DASHBOARD_SPEED_LANDING_TABLE,
  CHART_KEY_DASHBOARD_SPEED_LANDING_TIME_BAR,
  CHART_KEY_DASHBOARD_SPEED_LANDING_TIME_ON_PAGE,
} from "features/analytics/constants/chartKeys";
import { table5Rows } from "features/analytics/constants/constants";

interface ISpeedByLandingTabProps {
  attributes: ApiChartAttribute[];
}

function SpeedByLandingTab({ attributes }: ISpeedByLandingTabProps) {
  return (
    <>
      <CarouselCard
        title="Key Stats"
        emptyMessage="No key stats available"
        items={[
          <StandardAnalyticsChartStatCard
            chartKey={CHART_KEY_DASHBOARD_SPEED_LANDING_CWV}
            staticAttributes={attributes}
            chart={{
              name: "Landing CWV Score",
              dimension: [ApiDimension.DIMENSION_NOT_SET],
              template: ApiChartTemplate.SNAPSHOT,
              type: ApiChartType.STAT_CARD,
              attributes: [],
              yAxisKeys: [
                {
                  key: "l",
                  fields: [ApiMetric.METRIC_AVERAGE_CWV_PERFORMANCE_SCORE],
                },
              ],
              displayOptions: {
                statTitle: "Landing CWV Score",
              },
              comparison: true,
            }}
          />,
          <StandardAnalyticsChartStatCard
            chartKey={CHART_KEY_DASHBOARD_SPEED_LANDING_BOUNCE}
            staticAttributes={attributes}
            chart={{
              name: "Bounce Rate",
              dimension: [ApiDimension.DIMENSION_NOT_SET],
              template: ApiChartTemplate.SNAPSHOT,
              type: ApiChartType.STAT_CARD,
              attributes: [],
              yAxisKeys: [
                {
                  key: "l",
                  fields: [ApiMetric.METRIC_BOUNCE_RATE],
                },
              ],
              displayOptions: {
                statTitle: "Bounce Rate",
              },
              comparison: true,
            }}
          />,
          <StandardAnalyticsChartStatCard
            chartKey={CHART_KEY_DASHBOARD_SPEED_LANDING_TIME_ON_PAGE}
            staticAttributes={attributes}
            chart={{
              name: "Avg. Time on Page",
              dimension: [ApiDimension.DIMENSION_NOT_SET],
              template: ApiChartTemplate.SNAPSHOT,
              type: ApiChartType.STAT_CARD,
              attributes: [],
              yAxisKeys: [
                {
                  key: "l",
                  fields: [ApiMetric.METRIC_AVERAGE_TIME_ON_PAGE],
                },
              ],
              displayOptions: {
                statTitle: "Avg. Time on Page",
              },
              comparison: true,
            }}
          />,
        ]}
      />

      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={2}
        sx={{ flexShrink: 0, width: "100%", minWidth: 0 }}
      >
        <Box sx={{ height: { xs: 320, md: 400 }, flex: 1, minWidth: 0 }}>
          <StandardAnalyticsCard
            chartKey={CHART_KEY_DASHBOARD_SPEED_LANDING_CWV_BAR}
            staticAttributes={attributes}
            ignoreAttrModel
            chart={{
              name: "Landing Pages by CWV",
              dimension: [ApiDimension.DIMENSION_LANDING_PAGE_LOCATION],
              template: ApiChartTemplate.SNAPSHOT,
              type: ApiChartType.GRAPH,
              attributes: [],
              limit: 10,
              orderBy: `${ApiMetric.METRIC_AVERAGE_CWV_PERFORMANCE_SCORE} ASC`,
              having: [
                {
                  metric: ApiMetric.METRIC_PAGEVIEWS_COUNT,
                  operator: ApiOperator.IS_GREATER_THAN,
                  value: 0,
                },
                {
                  metric: ApiMetric.METRIC_AVERAGE_CWV_PERFORMANCE_SCORE,
                  operator: ApiOperator.IS_GREATER_THAN,
                  value: 0,
                },
              ],
              yAxisKeys: [
                {
                  key: "l",
                  fields: [ApiMetric.METRIC_AVERAGE_CWV_PERFORMANCE_SCORE],
                  chart: {
                    [ApiMetric.METRIC_AVERAGE_CWV_PERFORMANCE_SCORE]:
                      ApiYAxisChartType.BAR,
                  },
                },
              ],
              displayOptions: {
                noFilters: true,
                noAttributionToggle: true,
                layout: ApiChartLayout.VERTICAL,
                noLegend: true,
              },
            }}
          />
        </Box>

        <Box sx={{ height: { xs: 320, md: 400 }, flex: 1, minWidth: 0 }}>
          <StandardAnalyticsCard
            chartKey={CHART_KEY_DASHBOARD_SPEED_LANDING_TIME_BAR}
            staticAttributes={attributes}
            ignoreAttrModel
            chart={{
              name: "Avg. Time on Page for Lowest Performing Pages",
              dimension: [ApiDimension.DIMENSION_LANDING_PAGE_LOCATION],
              template: ApiChartTemplate.SNAPSHOT,
              type: ApiChartType.GRAPH,
              attributes: [],
              limit: 10,
              orderBy: `${ApiMetric.METRIC_AVERAGE_CWV_PERFORMANCE_SCORE} ASC`,
              having: [
                {
                  metric: ApiMetric.METRIC_PAGEVIEWS_COUNT,
                  operator: ApiOperator.IS_GREATER_THAN,
                  value: 0,
                },
                {
                  metric: ApiMetric.METRIC_AVERAGE_CWV_PERFORMANCE_SCORE,
                  operator: ApiOperator.IS_GREATER_THAN,
                  value: 0,
                },
              ],
              yAxisKeys: [
                {
                  key: "l",
                  fields: [ApiMetric.METRIC_AVERAGE_TIME_ON_PAGE],
                  chart: {
                    [ApiMetric.METRIC_AVERAGE_TIME_ON_PAGE]:
                      ApiYAxisChartType.BAR,
                  },
                },
              ],
              displayOptions: {
                noFilters: true,
                noAttributionToggle: true,
                layout: ApiChartLayout.VERTICAL,
                noLegend: true,
              },
            }}
          />
        </Box>
      </Stack>

      <Box
        sx={{
          height: table5Rows,
          flexShrink: 0,
          width: "100%",
          minWidth: 0,
          overflowX: "auto",
        }}
      >
        <StandardAnalyticsCard
          chartKey={CHART_KEY_DASHBOARD_SPEED_LANDING_TABLE}
          staticAttributes={attributes}
          ignoreAttrModel
          chart={{
            name: "Landing Page Speed",
            dimension: [ApiDimension.DIMENSION_LANDING_PAGE_LOCATION],
            template: ApiChartTemplate.TABLE,
            type: ApiChartType.TABLE,
            attributes: [],
            orderBy: `${ApiMetric.METRIC_AVERAGE_CWV_PERFORMANCE_SCORE} ASC`,
            yAxisKeys: [
              {
                key: "l",
                fields: [
                  ApiMetric.METRIC_PAGEVIEWS_COUNT,
                  ApiMetric.METRIC_AVERAGE_CWV_PERFORMANCE_SCORE,
                  ApiMetric.METRIC_AVERAGE_TIME_ON_PAGE,
                  ApiMetric.METRIC_EFFECTIVE_PAGEVIEW_RATE,
                  ApiMetric.METRIC_BOUNCE_RATE,
                  ApiMetric.METRIC_REVENUE,
                ],
              },
            ],
            displayOptions: {
              noFilters: true,
              noAttributionToggle: true,
              rowsPerPage: 0,
              showTotals: true,
            },
          }}
        />
      </Box>
    </>
  );
}

export default SpeedByLandingTab;
